import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhotoIcon, FilmIcon } from '@heroicons/react/24/solid';
import type { TvPhoto, TvTournament } from '../hooks/useTvData';

interface Props {
  tournament: TvTournament | null;
  photos: TvPhoto[];
}

const SLIDE_DURATION = 6000;
const GALLERY_SWITCH_DURATION = 30000;

function groupByGallery(photos: TvPhoto[]): Record<string, TvPhoto[]> {
  const map: Record<string, TvPhoto[]> = {};
  for (const p of photos) {
    const key = p.gallery_name || 'Gallery';
    if (!map[key]) map[key] = [];
    map[key].push(p);
  }
  return map;
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
}

interface MediaItemProps {
  photo: TvPhoto;
  isActive: boolean;
}

function MediaItem({ photo, isActive }: MediaItemProps) {
  const isVideo = isVideoUrl(photo.url);

  if (isVideo) {
    return (
      <video
        src={photo.url}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay={isActive}
        loop
        muted
        playsInline
      />
    );
  }

  return (
    <img
      src={photo.url}
      alt={photo.caption ?? ''}
      className="absolute inset-0 w-full h-full object-cover"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

export default function TvMultimediaSlide({ tournament, photos }: Props) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);

  const grouped = groupByGallery(photos);
  const galleries = Object.entries(grouped);

  const activeGalleryName = galleries[activeGalleryIndex]?.[0] ?? '';
  const activePhotos = galleries[activeGalleryIndex]?.[1] ?? [];
  const activePhoto = activePhotos[activePhotoIndex] ?? null;

  useEffect(() => {
    setActivePhotoIndex(0);
  }, [activeGalleryIndex]);

  useEffect(() => {
    if (activePhotos.length <= 1) return;
    const t = setInterval(() => {
      setActivePhotoIndex((i) => (i + 1) % activePhotos.length);
    }, SLIDE_DURATION);
    return () => clearInterval(t);
  }, [activePhotos.length, activeGalleryIndex]);

  useEffect(() => {
    if (galleries.length <= 1) return;
    const t = setInterval(() => {
      setActiveGalleryIndex((i) => (i + 1) % galleries.length);
    }, GALLERY_SWITCH_DURATION);
    return () => clearInterval(t);
  }, [galleries.length]);

  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-golf-gray text-white">
        <p className="text-white/30 text-xl sm:text-2xl">No media available</p>
      </div>
    );
  }

  const totalVisible = Math.min(activePhotos.length, 20);

  return (
    <div className="flex h-full bg-black text-white overflow-hidden">
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="crossfade">
          {activePhoto && (
            <motion.div
              key={`${activeGalleryName}-${activePhotoIndex}`}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
            >
              <MediaItem photo={activePhoto} isActive />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="absolute top-0 left-0 right-0 flex items-center gap-3 sm:gap-4 px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-black/50 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              {activePhoto && isVideoUrl(activePhoto.url)
                ? <FilmIcon className="w-4 h-4 sm:w-5 sm:h-5 text-golf-orange" />
                : <PhotoIcon className="w-4 h-4 sm:w-5 sm:h-5 text-golf-orange" />
              }
            </div>
            <div>
              <p className="text-white/40 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
                {tournament?.name ?? 'Tournament'}
              </p>
              <p className="text-white font-bold text-sm sm:text-lg leading-tight">{activeGalleryName}</p>
            </div>
          </div>

          {galleries.length > 1 && (
            <div className="ml-auto flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
              {galleries.map(([name], i) => (
                <button
                  key={name}
                  onClick={() => setActiveGalleryIndex(i)}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-all ${
                    i === activeGalleryIndex
                      ? 'bg-golf-orange text-white shadow-lg shadow-golf-orange/30'
                      : 'bg-black/40 text-white/40 hover:text-white/70 hover:bg-white/10 backdrop-blur-sm border border-white/10'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowThumbnails((v) => !v)}
            className="lg:hidden ml-auto flex-shrink-0 w-8 h-8 rounded-lg bg-black/50 border border-white/15 flex items-center justify-center"
            title="Toggle thumbnails"
          >
            <PhotoIcon className="w-4 h-4 text-white/60" />
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 pb-4 sm:pb-8">
          <AnimatePresence mode="wait">
            {activePhoto?.caption && (
              <motion.p
                key={`caption-${activePhotoIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="text-white/80 text-base sm:text-xl font-medium mb-3 sm:mb-4 drop-shadow-lg max-w-2xl"
              >
                {activePhoto.caption}
              </motion.p>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-1 sm:gap-1.5">
            {Array.from({ length: totalVisible }).map((_, i) => (
              <button
                key={i}
                onClick={() => setActivePhotoIndex(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activePhotoIndex
                    ? 'w-6 sm:w-8 h-1.5 sm:h-2 bg-golf-orange shadow-sm shadow-golf-orange/50'
                    : 'w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/25 hover:bg-white/50'
                }`}
              />
            ))}
            {activePhotos.length > 20 && (
              <span className="text-white/30 text-[10px] sm:text-xs ml-1">+{activePhotos.length - 20}</span>
            )}
          </div>
        </div>
      </div>

      <div className={`w-56 sm:w-64 lg:w-72 flex-shrink-0 flex flex-col border-l border-white/10 bg-black/60 backdrop-blur overflow-hidden transition-all duration-300 ${
        showThumbnails ? 'flex' : 'hidden lg:flex'
      }`}>
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-white/10 flex-shrink-0">
          <p className="text-white/30 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
            {activeGalleryName}
          </p>
          <p className="text-white/60 text-xs sm:text-sm mt-0.5">
            {activePhotoIndex + 1} of {activePhotos.length} {isVideoUrl(activePhoto?.url ?? '') ? 'videos' : 'photos'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 sm:p-3 grid grid-cols-2 gap-1.5 sm:gap-2 content-start">
          {activePhotos.slice(0, 20).map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActivePhotoIndex(i)}
              className={`relative aspect-square rounded-lg sm:rounded-xl overflow-hidden transition-all ${
                i === activePhotoIndex
                  ? 'ring-2 ring-golf-orange ring-offset-1 ring-offset-black scale-100'
                  : 'opacity-50 hover:opacity-75'
              }`}
            >
              {isVideoUrl(p.url) ? (
                <div className="w-full h-full bg-white/10 flex items-center justify-center">
                  <FilmIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white/40" />
                </div>
              ) : (
                <img
                  src={p.url}
                  alt={p.caption ?? ''}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).parentElement!.style.background = 'rgba(255,255,255,0.05)';
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              {i === activePhotoIndex && (
                <div className="absolute inset-0 bg-golf-orange/10" />
              )}
            </button>
          ))}
        </div>

        {galleries.length > 1 && (
          <div className="border-t border-white/10 p-3 sm:p-4 flex-shrink-0 space-y-1.5 sm:space-y-2">
            <p className="text-white/25 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1.5 sm:mb-2">All Galleries</p>
            {galleries.map(([name, gPhotos], i) => (
              <button
                key={name}
                onClick={() => setActiveGalleryIndex(i)}
                className={`w-full flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm transition-all ${
                  i === activeGalleryIndex
                    ? 'bg-golf-orange/20 text-golf-orange border border-golf-orange/30'
                    : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                }`}
              >
                <span className="font-medium truncate">{name}</span>
                <span className="text-[10px] sm:text-xs opacity-60 ml-2 flex-shrink-0">{gPhotos.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
